/**
 * Auth service — the SPA's half of the contract in `@backend`.
 *
 *   POST /auth/login    { email, password }   → TokenPair
 *   POST /auth/refresh  { refresh_token }     → TokenPair
 *   GET  /auth/me                             → MeResponse
 *   POST /auth/logout                         → 204
 *
 * E-mail links (W13.M3, `@auth`):
 *
 *   POST /auth/password-reset { email }       → 202, whatever the address
 *   POST /auth/links/check    { token }       → LinkInfo, or 400 "no longer valid"
 *   POST /auth/set-password   { token, password } → TokenPair (signs the person in)
 *
 * TokenPair is `{ access_token, refresh_token, token_type, expires_in }`.
 * Note `expires_in` — *seconds from now*, not a timestamp; it is converted
 * to an absolute epoch here so nothing downstream has to remember when the
 * response arrived.
 *
 * Refresh returns a **whole new pair**: the backend rotates the refresh
 * token on every use, so storing only the new access token would strand the
 * session at the old refresh token's expiry.
 *
 * Mock mode still answers instantly from fixtures — see `isLive('auth')`
 * for why that is a per-domain switch rather than one global flag.
 */
import { apiResolve, http, setTokens, clearTokens, HttpError, ValidationError } from '@/api'
import { isLive } from '@/api/config'
import { createUser } from '@/entities/user'
import type { User } from '@/entities/user'
import auth from '@/mock/auth.json'
import users from '@/mock/users.json'

/** What the API returns from /auth/me. */
export interface MeResponse {
  id: number
  firm_id: number | null
  email: string
  display_name: string
  user_kind: string
  lawyer_role?: string | null
  auth_method: string
}

interface TokenPair {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

export interface Credentials {
  email: string
  password: string
}

/** What a reset or invitation link is for, and whose password it sets. */
export interface LinkInfo {
  purpose: 'reset' | 'invite'
  email: string
  expiresAt: string
}

interface ApiLinkInfo {
  purpose: 'reset' | 'invite'
  email: string
  expires_at: string
}

/**
 * The API's one answer for an expired, used, unknown or malformed link is a
 * 400; a token too short to be one fails validation first (422). Both mean
 * the same thing to the person holding it.
 */
export function isInvalidLink(err: unknown): boolean {
  return err instanceof ValidationError || (err instanceof HttpError && err.status === 400)
}

/** The shortest password the API accepts (`SetPasswordRequest`, `UserUpdate`). */
export const MIN_PASSWORD = 12

const LIVE = () => isLive('auth')

/** Human labels for the three account tiers (D-015). */
const KIND_LABELS: Record<string, string> = {
  firm_main: 'Main account',
  firm_manager: 'Manager',
  client_viewer: 'Client viewer',
  superadmin: 'SuperAdmin',
}

/**
 * First letters of the first two words.
 *
 * Uses spread rather than `charAt`, because the seed is full of Cyrillic
 * and a surrogate pair would otherwise be sliced in half.
 */
export function initialsOf(name: string): string {
  return (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => [...w][0] ?? '')
    .join('')
    .toUpperCase()
}

/** MeResponse → the `User` shape the sidebar, topbar and account popup read. */
/** `senior_associate` → `Senior associate`. The column stores snake_case. */
function humanise(value: string): string {
  const spaced = value.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function userFromMe(me: MeResponse, firmName = ''): User {
  const role = me.lawyer_role
    ? humanise(me.lawyer_role)
    : (KIND_LABELS[me.user_kind] ?? me.user_kind)
  return createUser({
    id: me.id,
    name: me.display_name || me.email,
    initials: initialsOf(me.display_name || me.email),
    role,
    email: me.email,
    workspace: firmName,
    firmId: me.firm_id,
    userKind: me.user_kind,
    authMethod: me.auth_method,
  })
}

function storePair(pair: TokenPair): void {
  setTokens({
    accessToken: pair.access_token,
    refreshToken: pair.refresh_token,
    expiresAt: Math.floor(Date.now() / 1000) + (pair.expires_in ?? 0),
  })
}

export default {
  async me(): Promise<User | null> {
    if (!LIVE()) {
      const u = users.find((x) => x.id === auth.currentUserId) || null
      return apiResolve(u ? createUser({ ...u, workspace: auth.workspace }) : null, 60)
    }
    /* Deliberately NOT noRefresh. This is the session-restore path: after a
     * reload with a lapsed access token, a 401 here should spend the refresh
     * token, not end the session. Looping is impossible — /auth/refresh opts
     * out itself, and the interceptor replays any request only once. */
    const me: MeResponse = await http.get('/auth/me')
    if (!me) return null
    /* The firm's display name is not part of MeResponse, and the account
     * popup shows "role · workspace". A viewer can read their own firm, so
     * this is safe for every tier — but it must never turn a good session
     * into a failed sign-in, hence the swallow. */
    const firmName = await http
      .get('/firm')
      .then((firm) => firm?.name ?? '')
      .catch(() => '')
    return userFromMe(me, firmName)
  },

  async signIn(credentials: Credentials) {
    if (!LIVE()) return apiResolve({ ok: true })
    /* noRefresh: a 401 here means "wrong password", not "stale token".
     * Letting the refresh interceptor see it would fire a pointless
     * refresh and report the wrong failure to the user. */
    const pair: TokenPair = await http.post('/auth/login', credentials, {
      meta: { noRefresh: true },
    })
    storePair(pair)
    return pair
  },

  /** Exchange the refresh token for a fresh pair. Throws if it is dead. */
  async refresh() {
    if (!LIVE()) return apiResolve({ ok: true })
    const { getRefreshToken } = await import('@/api/tokens')
    const refresh_token = getRefreshToken()
    if (!refresh_token) throw new Error('No refresh token')
    const pair: TokenPair = await http.post(
      '/auth/refresh',
      { refresh_token },
      { meta: { noRefresh: true, noRetry: true } }
    )
    storePair(pair)
    return pair
  },

  /**
   * Ask for a reset link. The API answers 202 whether or not the address has
   * an account — so this resolves the same way for both, and the page says the
   * same thing for both. `noRefresh`: nobody is signed in on this path.
   */
  async requestPasswordReset(email: string): Promise<void> {
    if (!LIVE()) {
      await apiResolve(null, { path: '/auth/password-reset', method: 'POST' })
      return
    }
    await http.post(
      '/auth/password-reset',
      { email: email.trim() },
      { meta: { noRefresh: true, noRetry: true } }
    )
  },

  /**
   * What a link from an e-mail is for, before anything is typed. The token
   * travels in the body, never in a URL the API logs.
   */
  async checkLink(token: string): Promise<LinkInfo> {
    if (!LIVE()) {
      await apiResolve(null, { path: '/auth/links/check', method: 'POST' })
      if (!token || token.includes('expired')) {
        throw new HttpError(400, 'This link is no longer valid. Ask for a new one.')
      }
      const me = users.find((x) => x.id === auth.currentUserId)
      const invite = token.includes('invite')
      /* The API's lifetimes (docs/auth.md): a reset link 30 minutes, an
       * invitation 7 days. */
      const lifetime = invite ? 7 * 24 * 60 : 30
      return {
        purpose: invite ? 'invite' : 'reset',
        email: me?.email ?? 'someone@example.com',
        expiresAt: new Date(Date.now() + lifetime * 60_000).toISOString(),
      }
    }
    const info: ApiLinkInfo = await http.post(
      '/auth/links/check',
      { token },
      { meta: { noRefresh: true, noRetry: true } }
    )
    return { purpose: info.purpose, email: info.email, expiresAt: info.expires_at }
  },

  /**
   * Spend the link: the API sets the password and answers with a token pair —
   * the same pair a sign-in returns — which is stored the same way.
   */
  async setPassword(token: string, password: string) {
    if (!LIVE()) return apiResolve({ ok: true })
    const pair: TokenPair = await http.post(
      '/auth/set-password',
      { token, password },
      { meta: { noRefresh: true, noRetry: true } }
    )
    storePair(pair)
    return pair
  },

  async signOut() {
    if (!LIVE()) {
      clearTokens()
      return apiResolve(true, 40)
    }
    try {
      await http.post('/auth/logout', undefined, { meta: { noRefresh: true } })
    } catch {
      /* Signing out locally must succeed even when the server cannot be
       * reached — the token is cleared either way, below. */
    } finally {
      clearTokens()
    }
  },
}
